import { $enum } from "ts-enum-util";

import { PlanningDay, type Planning } from "@eisox/api/core-api";

import { mergeTimeslot } from "./planning.helper";
import { type UpdateSchemaType } from "./schema.helper";
import { hourEndToMinutes, toMinutes, toTimeString } from "./time.helper";

const days = $enum(PlanningDay).getValues();

const generateAllHours = (): string[] =>
  Array.from({ length: 24 }, (_, h) =>
    [0, 15, 30, 45].map(m => `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`),
  ).flat();

const getHoursStart = (endHour?: string, dayStart?: PlanningDay, dayEnd?: PlanningDay): string[] => {
  // Si ce n'est pas le même jour, toutes les heures sont disponibles
  if (dayStart !== dayEnd || !endHour) return generateAllHours();

  const endMinutes = hourEndToMinutes(endHour);
  // Une heure non parsable ne contraint rien
  if (Number.isNaN(endMinutes)) return generateAllHours();

  return generateAllHours().filter(h => {
    const startMinutes = toMinutes(h);
    // Pour le même jour, on empêche les plages qui ne vont pas dans le sens horaire
    // L'heure de début doit être strictement inférieure à l'heure de fin
    // Et il doit y avoir au moins 1 heure (60 minutes) d'écart
    return startMinutes < endMinutes && endMinutes - startMinutes >= 60;
  });
};

const getHoursEnd = (startHour?: string, dayStart?: PlanningDay, dayEnd?: PlanningDay): string[] => {
  // Si ce n'est pas le même jour, toutes les heures sont disponibles
  if (dayStart !== dayEnd || !startHour) return generateAllHours();

  const startMinutes = toMinutes(startHour);
  // Une heure non parsable ne contraint rien
  if (Number.isNaN(startMinutes)) return generateAllHours();

  return generateAllHours().filter(h => {
    const endMinutes = toMinutes(h);
    // Pour le même jour, on empêche les plages qui ne vont pas dans le sens horaire
    // L'heure de fin doit être strictement supérieure à l'heure de début
    // Et il doit y avoir au moins 1 heure (60 minutes) d'écart
    return endMinutes > startMinutes && endMinutes - startMinutes >= 60;
  });
};

const getNextDay = (currentDay: PlanningDay): PlanningDay => {
  const nextDayIndex = (days.indexOf(currentDay) + 1) % days.length;
  return days[nextDayIndex] ?? currentDay;
};

const sameDay = (update: {
  begin: { day: PlanningDay | undefined; hour: string | undefined } | undefined;
  end: { day: PlanningDay | undefined; hour: string | undefined } | undefined;
}): boolean => {
  const dayStart = update.begin?.day;
  const dayEnd = update.end?.day;
  const hourStart = update.begin?.hour;
  const hourEnd = update.end?.hour;

  if (!dayStart || !dayEnd || !hourStart || !hourEnd) return false;

  const beginMinutes = toMinutes(hourStart);
  const endMinutes = hourEndToMinutes(hourEnd);

  if (dayStart === dayEnd && beginMinutes < endMinutes) return true;

  if (getNextDay(dayStart) === dayEnd && hourEnd === "00:00") return true;

  return false;
};

const convertUpdateSchemaTypeToPlanning = (update: UpdateSchemaType): Planning[] => {
  const { begin, end, type } = update;

  const startIndex = days.indexOf(begin.day);
  const endIndex = days.indexOf(end.day);

  if (startIndex === -1 || endIndex === -1) return [];

  const result: Planning[] = [];
  let currentDayIndex = startIndex;
  let startSegment = toMinutes(begin.hour);
  const targetDayIndex = endIndex;
  const targetEnd = hourEndToMinutes(end.hour);

  while (true) {
    const isTargetDay = currentDayIndex === targetDayIndex;
    let endSegment = isTargetDay ? targetEnd : 24 * 60;

    // If the timeslot naturally spans across days and ends precisely at 00:00 on the target day,
    // we do not want to create an empty 0-duration slot for the target day.
    if (isTargetDay && end.hour === "00:00" && startIndex !== endIndex) endSegment = 0;

    if (startSegment < endSegment) {
      const day = days[currentDayIndex];
      if (day) {
        result.push({
          day,
          hourStart: toTimeString(startSegment),
          hourEnd: toTimeString(endSegment),
          type: type,
          userDefined: true,
        });
      }
    }

    if (isTargetDay) break;
    currentDayIndex = (currentDayIndex + 1) % days.length;
    startSegment = 0;
  }

  return result;
};

const merge = (updating: UpdateSchemaType, existing: Planning[]): Planning[] => {
  let sourcePlannings = convertUpdateSchemaTypeToPlanning(updating);
  if (updating.copyToOtherDays) {
    sourcePlannings = [
      ...sourcePlannings,
      ...sourcePlannings.flatMap(p => updating.targetDays.filter(d => d !== p.day).map(day => ({ ...p, day }))),
    ];
  }
  if (existing.length === 0) return sourcePlannings;

  const plannings = [...existing, ...sourcePlannings];
  const mergedPlannings = mergeTimeslot(plannings);

  return mergedPlannings;
};

export { convertUpdateSchemaTypeToPlanning, getNextDay, getHoursStart, getHoursEnd, generateAllHours, merge, sameDay };
